import React, { CSSProperties } from "react";
import { useNotification } from "@refinedev/core";
import { Column, flexRender, Table as T } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

import {
  ArrowDown,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUp,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import { Card, CardContent } from "@/components/ui/card";
import { ColumnResizer } from "@/components/refine-ui/data-table/column-resizer";
import { Button } from "@/components/ui/button";
import { UseTableReturnType } from "@refinedev/react-table";
import { cn } from "@/lib/utils";
import { Table as TableType } from "@tanstack/react-table";

// src [https://tanstack.com/table/v8/docs/framework/react/examples/column-pinning-sticky]
const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: isPinned ? 1 : 1,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 10 : 0,
  };
};

export type DeprecatedDataTableProps = {
  tableProps: TableType<any>;
  contextMenuOptions?: {
    label: string;
    shortcut?: string;
    action?: (data: any) => void;
  }[];
  onRowKeyEvent?: (
    e: KeyboardEvent | React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    data: any,
    type: "mouse" | "keyboard",
  ) => void;
};

/**
 * @deprecated DeprecatedDataTable is deprecated and will be removed in a future release.
 * Please migrate to the new table component (e.g. DeprecatedDataTable in src/component/refine-ui/data-table/data-table).
 */
export function DeprecatedDataTable({
  contextMenuOptions,
  tableProps,
  onRowKeyEvent,
}: DeprecatedDataTableProps) {
  const {
    getHeaderGroups,
    getVisibleLeafColumns,
    getRowModel,

    getState,
    setPageIndex,
    getCanPreviousPage,
    getPageCount,
    getCanNextPage,
    nextPage,
    previousPage,
    setPageSize,
    getColumn,
    getRowCount,
    getPrePaginationRowModel,
  } = tableProps;

  const { open } = useNotification();

  return (
    <Card>
      <CardContent>
        <Table className="table-fixed">
          <TableHeader>
            {getHeaderGroups().map((headerGroup: any) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <TableHead
                    key={header.id}
                    className="relative bg-background"
                    style={{
                      width: header.getSize(),
                      ...getCommonPinningStyles(header.column),
                    }}
                  >
                    <>
                      {!header.column.getCanSort() ? (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      ) : (
                        <Button
                          variant="ghost"
                          className="rounded-md"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === "asc" ? (
                              <ArrowUp className="ml-2 h-4 w-4" />
                            ) : (
                              <ArrowDown className="ml-2 h-4 w-4" />
                            )
                          ) : (
                            <></>
                          )}
                        </Button>
                      )}
                      <ColumnResizer header={header} />
                    </>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {getRowModel().rows.map((row: any) => {
              const copy = () => {
                const id = row.getValue("id");
                navigator.clipboard.writeText(id);
                open?.({
                  message: `"${id}" copied to clipboard`,
                  type: "success",
                });
              };

              const copyId = (e: KeyboardEvent) => {
                if (e.key === "." && (e.ctrlKey || e.metaKey)) {
                  copy();
                }
                onRowKeyEvent?.(e, row.original, "keyboard");
              };

              return (
                <TableRow
                  key={row.id}
                  className="truncate group"
                  onMouseEnter={() => {
                    document.addEventListener("keydown", copyId);
                  }}
                  onMouseLeave={() => {
                    document.removeEventListener("keydown", copyId);
                  }}
                  // onMouseUp={(e) => onRowKeyEvent?.(e, row.original, "mouse")}
                >
                  {row.getVisibleCells().map((cell: any) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "px-4 py-0 bg-background",
                        "group-hover:bg-background/10",
                      )}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.columnDef.minSize,
                        ...getCommonPinningStyles(cell.column),
                      }}
                    >
                      <ContextMenu>
                        <ContextMenuTrigger>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </ContextMenuTrigger>
                        <ContextMenuContent className="w-64 text-foreground">
                          {contextMenuOptions?.map((option, idx) => (
                            <ContextMenuItem
                              key={idx}
                              onClick={() => option.action?.(row.original)}
                            >
                              {option.label}
                              {option.shortcut && (
                                <ContextMenuShortcut>
                                  {option.shortcut}
                                </ContextMenuShortcut>
                              )}
                            </ContextMenuItem>
                          ))}
                        </ContextMenuContent>
                      </ContextMenu>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationLink onClick={() => setPageIndex(0)}>
                <ArrowLeftToLine className="h-4 w-4" />
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationPrevious onClick={() => previousPage()}>
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationPrevious>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={() => nextPage()}>
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationNext>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink onClick={() => setPageIndex(getPageCount() - 1)}>
                <ArrowRightToLine className="h-4 w-4" />
              </PaginationLink>
            </PaginationItem>
            <div className="flex items-center space-x-6 lg:space-x-8">
              <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                Page {getState().pagination.pageIndex + 1} of {getPageCount()}
              </div>
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium">Rows per page</p>
                <Select
                  value={`${getState().pagination.pageSize}`}
                  onValueChange={(value: any) => {
                    setPageSize(Number(value));
                  }}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={getState().pagination.pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <SelectItem key={pageSize} value={`${pageSize}`}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm font-medium">Total Rows{getRowCount()}</p>
              </div>
            </div>
          </PaginationContent>
        </Pagination>
      </CardContent>
    </Card>
  );
}
